import { Address } from 'viem'
import { Config } from 'wagmi'
import { readContract } from 'wagmi/actions'

import Erc20Abi from '../abi/erc20'

// See https://eips.ethereum.org/EIPS/eip-5267

export type Eip712Domain = Omit<Eip5267Return, 'fields' | 'extensions'>

type Eip5267Return = {
  fields: string
  name?: string
  version?: string
  chainId?: string
  verifyingContract?: string
  salt?: string
  extensions?: string[]
}

export async function getEip712Domain(tokenAddress: Address, chainId: number, config: Config): Promise<Eip712Domain> {
  const [fields, name, version, domainChainId, verifyingContract, salt, extensions] = await readContract(config, {
    abi: Erc20Abi,
    address: tokenAddress,
    functionName: 'eip712Domain',
  })
  const eip5267Domain: Required<Eip5267Return> = {
    fields,
    name,
    version,
    chainId: domainChainId.toString(),
    verifyingContract,
    salt,
    extensions: extensions.map((e: unknown) => String(e)),
  }

  return processDomain(eip5267Domain, chainId, tokenAddress)
}

/** Retrieves the EIP-712 domain of a contract using EIP-5267 without extensions.
 *
 * Derived from https://eips.ethereum.org/EIPS/eip-5267 reference Javascript implementation
 **/
async function processDomain(domain: Required<Eip5267Return>, chainId: number, address: string): Promise<Eip712Domain> {
  const { extensions, ...rest } = domain

  if (extensions.length > 0) {
    throw Error('Extensions not implemented')
  }

  if (rest.chainId && chainId !== parseInt(rest.chainId, 16)) {
    throw Error(`ChainId mismatch. Received: '${rest.chainId}', expected: '${chainId}'`)
  }

  if (rest.verifyingContract && address.toLowerCase() !== rest.verifyingContract.toLowerCase()) {
    throw Error(`Address mismatch. Received: '${rest.verifyingContract}', expected: '${address}'`)
  }

  return buildBasicDomain(rest)
}

const FIELD_NAMES = ['name', 'version', 'chainId', 'verifyingContract', 'salt']

/** Builds a domain object without extensions based on the return values of `eip712Domain()`. */
function buildBasicDomain(domain: Required<Omit<Eip5267Return, 'extensions'>>): Eip712Domain {
  // Will be a 5bit number as a hex string
  const fields = parseInt(domain.fields, 16)

  return FIELD_NAMES.reduce<Eip712Domain>((acc, field, i) => {
    if (fields & (1 << i)) {
      acc[field as keyof Eip712Domain] = domain[field as keyof Omit<Eip5267Return, 'extensions'>]
    }
    return acc
  }, {})
}
